import { map } from 'rxjs';
import { TtmTicket } from './../../classes/ttm-ticket.class';
import { Component, OnInit, signal, WritableSignal } from '@angular/core';
import { CodaltComponent } from '../codalt/codalt.component';
import { TtmTicketService } from '../../services/ttm-ticket.service';
import { ActivatedRoute } from '@angular/router';
import { TtmStatus } from '../../classes/ttmstatus';
import { TtmService } from '../../services/ttm.service';
import { TtmType } from '../../classes/ttmtype';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthorisationService } from '../../services/auth/authorisation.service';
import { LocalStorage } from '../../utils/storage';
import { Roles } from '../../services/auth/roles';
import { ConfirmDialogService } from '../../services/confirm-dialog-service/confirm-dialog.service';
import { UserService } from '../../services/user.service';
import { User } from '../../classes/user';
import { TtmTicketStatus } from '../../classes/ttm-ticket-status';

@Component({
  selector: 'app-ttm-ticket-detail',
  templateUrl: './ttm-ticket-detail.component.html',
  styleUrl: './ttm-ticket-detail.component.scss'
})
export class TtmTicketDetailComponent extends CodaltComponent implements OnInit {
    ttmTicket: WritableSignal<TtmTicket | null> = signal<TtmTicket | null>(null);

    ttmStatuses: TtmStatus[] = [];
    ttmTypes: TtmType[] = [];
    users: User[] = [];
    ticketStatuses: TtmTicketStatus[] = [];

    commentForm: FormGroup = new FormGroup({
        text: new FormControl('', [Validators.required, Validators.minLength(1)])
    });

    ticketForm: FormGroup = new FormGroup({
        comment: new FormControl('', [Validators.maxLength(1000)]),
        assignee_id: new FormControl(null),
        status: new FormControl('')
    });

    isEditingTicket = false;
    isEditingAssignee = false;

    currentUser = LocalStorage.user;

    constructor(
        private ttmTicketService: TtmTicketService,
        private ttmService: TtmService,
        private route: ActivatedRoute,
        private confirmDialogService: ConfirmDialogService,
        private userService: UserService
    ) {
        super();
    }

    ngOnInit() {
        const ticketId = this.route.snapshot.paramMap.get('ticketId');
        if (ticketId !== null) {
            this.subscriptions.add(
                this.ttmTicketService.getTicket(+ticketId).pipe(
                    map(response => response.data),
                ).subscribe(data => {
                    this.ttmTicket.set(data);
                })
            );

            const statuses$ = this.ttmService.statuses();
            const types$ = this.ttmService.types();

            this.subscriptions.add(
                statuses$.subscribe(response => {
                    this.ttmStatuses = response.data;
                })
            );

            this.subscriptions.add(
                types$.subscribe(response => {
                    this.ttmTypes = response.data;
                })
            );

            // Load users for assignee dropdown
            this.subscriptions.add(
                this.ttmTicketService.getAssignees().subscribe(response => {
                    this.users = response.data;
                })
            );

            // Load ticket statuses
            this.subscriptions.add(
                this.ttmTicketService.getTicketStatuses().subscribe(response => {
                    this.ticketStatuses = response.data;
                })
            );
        } else {
            // Handle missing ticketId, e.g. set ticket to null or show an error
            this.ttmTicket.set(null);
        }
    }

    onSubmitComment() {
        if (this.commentForm.valid && this.ttmTicket()) {
            const commentText = this.commentForm.get('text')?.value;
            const ticketId = this.ttmTicket()?.id;

            if (ticketId && commentText) {
                this.subscriptions.add(
                    this.ttmTicketService.addComment(ticketId, commentText).subscribe({
                        next: (response) => {
                            // Refresh the ticket data to show the new comment
                            this.subscriptions.add(
                                this.ttmTicketService.getTicket(ticketId).pipe(
                                    map(ticketResponse => ticketResponse.data)
                                ).subscribe(ticket => {
                                    this.ttmTicket.set(ticket);
                                })
                            );
                            // Reset the form
                            this.commentForm.reset();
                        },
                        error: (error) => {
                            console.error('Error adding comment:', error);
                        }
                    })
                );
            }
        }
    }

    deleteComment(comment: any) {
        this.confirmDialogService.confirm(
            'Opmerking verwijderen',
            `Weet je zeker dat je deze opmerking van ${comment.user?.name} wilt verwijderen?`,
            'Verwijderen',
            'Behouden'
        ).then(() => {
            const ticketId = this.ttmTicket()?.id;
            if (ticketId) {
                this.subscriptions.add(
                    this.ttmTicketService.deleteComment(ticketId, comment.id).subscribe({
                        next: () => {
                            // Refresh the ticket data to remove the deleted comment
                            this.subscriptions.add(
                                this.ttmTicketService.getTicket(ticketId).pipe(
                                    map(ticketResponse => ticketResponse.data)
                                ).subscribe(ticket => {
                                    this.ttmTicket.set(ticket);
                                })
                            );
                        },
                        error: (error) => {
                            console.error('Error deleting comment:', error);
                        }
                    })
                );
            }
        }, () => {
            // User canceled, do nothing
        });
    }

    startEditingTicket() {
        const ticket = this.ttmTicket();
        if (ticket) {
            this.ticketForm.patchValue({
                comment: ticket.comment || '',
                assignee_id: ticket.assignee_id || null,
                status: ticket.status || 'new'
            });
            this.isEditingTicket = true;
        }
    }

    cancelEditingTicket() {
        this.isEditingTicket = false;
        this.ticketForm.reset();
    }

    saveTicket() {
        if (this.ticketForm.valid && this.ttmTicket()) {
            const ticketId = this.ttmTicket()?.id;
            const commentText = this.ticketForm.get('comment')?.value;
            const assigneeId = this.ticketForm.get('assignee_id')?.value;
            const status = this.ticketForm.get('status')?.value;

            if (ticketId !== undefined) {
                this.subscriptions.add(
                    this.ttmTicketService.updateTicket(ticketId, { comment: commentText, assignee_id: assigneeId, status: status } as any).subscribe({
                        next: (response) => {
                            // Refresh the ticket to get updated data
                            this.subscriptions.add(
                                this.ttmTicketService.getTicket(ticketId).pipe(
                                    map(ticketResponse => ticketResponse.data)
                                ).subscribe(ticket => {
                                    this.ttmTicket.set(ticket);
                                })
                            );
                            this.isEditingTicket = false;
                        },
                        error: (error) => {
                            console.error('Error updating ticket:', error);
                        }
                    })
                );
            }
        }
    }

    toggleMuteNotifications() {
        const ticket = this.ttmTicket();
        if (!ticket || ticket.id === undefined) return;

        const actionText = ticket.is_muted?.email_enabled ? 'uitzetten' : 'aanzetten';
        const userHasGlobalNotifications = this.currentUser?.email_ticket_notifications ?? true;

        const skipModal = (userHasGlobalNotifications && ticket.is_muted?.email_enabled) ||
                          (!userHasGlobalNotifications && !ticket.is_muted?.email_enabled);

        if (skipModal) {
            // Just toggle this specific ticket
            const action$ = ticket.is_muted?.email_enabled
                ? this.ttmTicketService.muteNotifications(ticket.id!)
                : this.ttmTicketService.unmuteNotifications(ticket.id!);

            this.subscriptions.add(
                action$.subscribe({
                    next: () => {
                        this.subscriptions.add(
                            this.ttmTicketService.getTicket(ticket.id!).pipe(
                                map(response => response.data)
                            ).subscribe(ticket => {
                                this.ttmTicket.set(ticket);
                            })
                        );
                    },
                    error: (error) => {
                        console.error('Error toggling notification mute:', error);
                    }
                })
            );
            return;
        }

        // Ask user if they want to mute/unmute this ticket or all tickets
        this.confirmDialogService.confirm(
            `Meldingen ${actionText}`,
            `Wil je meldingen ${actionText} voor alleen dit ticket of voor alle tickets?`,
            'Alleen dit ticket',
            'Alle tickets'
        ).then(() => {
            // User chose "this ticket only"
            const action$ = ticket.is_muted?.email_enabled
                ? this.ttmTicketService.muteNotifications(ticket.id!)
                : this.ttmTicketService.unmuteNotifications(ticket.id!);

            this.subscriptions.add(
                action$.subscribe({
                    next: () => {
                        // Refresh ticket to get updated mute status
                        this.subscriptions.add(
                            this.ttmTicketService.getTicket(ticket.id!).pipe(
                                map(response => response.data)
                            ).subscribe(ticket => {
                                this.ttmTicket.set(ticket);
                            })
                        );
                    },
                    error: (error) => {
                        console.error('Error toggling notification mute:', error);
                    }
                })
            );
        }, () => {
            // User chose "all tickets"
            this.subscriptions.add(
                this.userService.toggleTicketNotifications().subscribe({
                    next: (response) => {
                        // Refresh ticket to reflect the change
                        this.subscriptions.add(
                            this.ttmTicketService.getTicket(ticket.id!).pipe(
                                map(ticketResponse => ticketResponse.data)
                            ).subscribe(ticket => {
                                this.ttmTicket.set(ticket);
                            })
                        );
                    },
                    error: (error) => {
                        console.error('Error toggling all ticket notifications:', error);
                    }
                })
            );
        });
    }
}
